import { Menu, MenuProps } from 'antd';
import { PresetStatusColorType } from 'antd/es/_util/colors';
import { ItemType, MenuItemType } from "antd/es/menu/interface";
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { FaCaretRight } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import { useAppGlobStore } from 'src/appGlobalStore';
import ErrorBoundary from 'src/content/errorBoundary/ErrorBoundary';
import { SearchBar, useSearchStore } from 'src/content/search';
import { API_URL } from 'src/data/Api';
import { RoutePath } from 'src/data/Routes';
import { menuItemsRight, resetMenuItemsRight } from 'src/data/menu-items';
import { useWebSocketLicense, useWebSocketReloadGetRandomNumberOnReload } from 'src/hook/useWebSocketReload';
import { MenuNode } from 'src/model/MenuNode';
import { Log } from 'src/service/log.service';
import { GLOB } from 'src/util/Glob';
import { WebsocketLicenseData } from '../../common/websocketMessage.dto';
import HamburgerMenuButton from "../left/Mobile/HamburgerMenuButton";
import './TopNavBar.less';

// take items and folders
const ITEM_URL = API_URL.CUSTOM_GROUP_MENU;

function hasCG(node: MenuNode) {
  if (!node) return false;
  if (node.url?.startsWith(ITEM_URL)) return true;
  if (node.children?.some(mn => hasCG(mn))) return true;
  return false;
}

function limitStatusToPresetColor(status: WebsocketLicenseData['status']): PresetStatusColorType {
  if (status === 'error') return 'error';
  if (status === 'warn') return 'warning';
  return 'default';
}

function removeTreeOnly(nodes: MenuNode[]): MenuNode[] {
  return nodes.filter(i => !i.treeOnly).map(i => {
    if (i.children) {
      return { ...i, children: removeTreeOnly([...i.children]) };
    }
    return i;
  });
}

function TopNavBar() {
  const info = useAppGlobStore(s => s.info);
  const showMenu = useAppGlobStore(s => s.showMenu);
  const topMenuSelectedKeys = useAppGlobStore(s => s.topMenuSelectedKeys);
  const topMenu = useAppGlobStore(s => s.topMenu);
  const isMobile = useAppGlobStore(s => s.isMobile);
  const availableTags = useSearchStore(s => s.availableTags);
  const availableArchitecture = useSearchStore(s => s.availableArchitecture);
  const refreshTagsData = useSearchStore(s => s.refreshTagsData);
  const loadArchitectureData = useSearchStore(s => s.loadArchitectureData);
  const location = useLocation();
  const defaultTopKey = useMemo(() => {
    if (location.pathname.startsWith(RoutePath.SEARCH)) return RoutePath.SEARCH;
    if (location.pathname.startsWith(RoutePath.PROFILE)) return RoutePath.PROFILE;
    if (location.pathname.startsWith(RoutePath.SETTINGS)) return RoutePath.SETTINGS;
    return undefined;
  }, [location.pathname]);
  const limit = useWebSocketLicense();
  const wsReload = useWebSocketReloadGetRandomNumberOnReload("top-menu");
  const [menuItemsFetched, setMenuItemsFetched] = useState<ItemType[]>([]);
  const [menuItemsStatic, setMenuItemsStatic] = useState<ItemType[]>([]);

  useEffect(() => {
    ErrorBoundary.reset();
  }, [location]);

  useEffect(() => {
    if (availableTags.length === 0) {
      void refreshTagsData();
    }
  }, [availableTags.length, refreshTagsData]);

  useEffect(() => {
    if (availableArchitecture.length === 0) {
      void loadArchitectureData();
    }
  }, [availableArchitecture.length, loadArchitectureData]);

  useEffect(() => {
    GLOB.xormonReady.promise
      .then(() => {
        void useSearchStore.getState().reloadHistoryData();
      })
      .catch((err) => Log.error('Failed to load initial history data:', err));
  }, []);

  useEffect(() => {
    if (defaultTopKey && !topMenuSelectedKeys.includes(defaultTopKey)) {
      useAppGlobStore.setState({ topMenuSelectedKeys: [defaultTopKey] });
    }
  }, [defaultTopKey, topMenuSelectedKeys]);

  useEffect(() => {
    if (!limit)
      return;

    if (limit.data.type === 'license')
      useAppGlobStore.setState({ licenseStatus: limitStatusToPresetColor(limit.data.status) });
  }, [limit]);

  useEffect(() => {
    if (wsReload === undefined)
      return;

    GLOB.menuService.init().catch(Log.error);
  }, [wsReload]);

  useEffect(() => {
    GLOB.xormonReady.promise.then(() => {
      const left: ItemType[] = useAppGlobStore.getState().topMenu.map(mn => {
        const commonProps: ItemType = {
          key: mn.key as string,
          popupClassName: SUBMENU_POPUP_CLASS,
          className: 'xm-menu-top-item'
        };

        switch (mn.path.toLocaleLowerCase()) {
          case RoutePath.STORAGE:
            return {
              ...commonProps,
              label: <Link to={mn.path + RoutePath.DEVICE}><>{mn.title}</></Link>,
              disabled: mn.notActive,
              children: [
                { key: 'storageDevice', label: <Link to={mn.path + RoutePath.DEVICE}>Devices</Link>, title: 'Storage devices' },
                { key: 'storageGroup', label: <Link to={mn.path + RoutePath.GROUP}>Groups</Link>, title: 'Storage devices by groups' },
                { key: 'storageType', label: <Link to={mn.path + RoutePath.TYPE}>HW Types</Link>, title: 'Storage devices by HW type' },
              ]
            };
          case RoutePath.SAN:
          case RoutePath.LAN_CFG:
          case RoutePath.TAPE:
            return {
              ...commonProps,
              disabled: mn.notActive,
              label: <Link to={mn.path + RoutePath.TYPE}><>{mn.title}</></Link>,
            };
          case RoutePath.DASHBOARD:
          case RoutePath.CUSTOM_GROUPS:
            return {
              ...commonProps,
              label: <Link to={mn.path + (mn.path === RoutePath.DASHBOARD ? RoutePath.DEFAULT : '')}><>{mn.title}</></Link>,
              disabled: mn.notActive || !mn.children?.length || mn.path === RoutePath.CUSTOM_GROUPS && !hasCG(mn),
              children: mn.children.map<ItemType>(child => {
                if (child.divider) {
                  return { type: 'divider', key: child.key };
                }
                return ({
                  key: child.key, label: <Link to={mn.path + GLOB.getItemPath(child)}><>{!child.isLeaf && <FaCaretRight style={{ verticalAlign: 'text-top' }} />}{child.title}</></Link>,
                  className: child.isLeaf ? null : 'xm-menu-folder'
                });
              })
            };
          default:
            return {
              ...commonProps,
              label: <Link to={mn.path + RoutePath.TYPE}><>{mn.title}</></Link>,
              disabled: mn.notActive,
              children: mn.children?.map<MenuItemType>(child => ({
                key: child.key, label: <Link to={mn.path + child.path}><>{child.title}</></Link>,
              }))
            };
        }
      });

      setMenuItemsFetched(left);
      resetMenuItemsRight();
      menuItemsRight.at(-1).children[0].title = (
        <span className="text-grayx" title={GLOB.userInfo?.email}>
          {GLOB.userInfo?.username}
        </span>
      );

      const rightTop: ItemType[] = removeTreeOnly([...filterMenu(menuItemsRight)]).map((mn): ItemType => {
        if (mn.key === RoutePath.SEARCH) {
          return {
            key: mn.key,
            icon: <div className='xm-icon-wrapper xm-search'><SearchBar /></div>
          };
        }

        if (!mn.children)
          return { key: mn.key, icon: mn.icon as ReactNode };

        return {
          key: mn.key,
          icon: mn.icon as ReactNode,
          popupClassName: SUBMENU_POPUP_CLASS,
          children: mn.children.map(makeChildRight(mn))
        };
      });

      setMenuItemsStatic(rightTop);
      GLOB.menuService.menuFiltered.resolve();
    }, console.debug);
  }, [topMenu]);

  const filterMenu = (nodes: MenuNode[]) => {
    return nodes.filter(mn => (!mn.notInDemo || !info?.backend?.demo)
      && (!mn.notInProduction || !info?.backend?.production)
      && (!mn.notOnDocker || !info?.backend?.docker)
      && (!mn.adminOnly || GLOB.userInfo.isAdmin)
      && (!mn.managerOnly || GLOB.userInfo.isManager || GLOB.userInfo.isAdmin)
      && (!mn.adminFeature || (!GLOB.userInfo.readonly && GLOB.userInfo.allowedFeatures[mn.adminFeature]))
      && (!mn.hwType || GLOB.menuService.hwTypes.some(ht => Array.isArray(mn.hwType) ? (mn.hwType.includes(ht.hw_type) && ht.allowed) : (ht.hw_type === mn.hwType) && (GLOB.userInfo.isAdmin || ht.allowed)))
      && (!mn.classType || GLOB.menuService.hwTypes.some(ht => ht.class === mn.classType && (GLOB.userInfo.isAdmin || ht.allowed))))
      .map(mn => {
        if (mn.children) {
          mn.children = filterMenu(mn.children);
        }
        return mn;
      }).filter(mn => mn.isLeaf || mn.children?.length || mn.path === RoutePath.SET_LOGS);
  };

  // const handleOpen = (openKeys: string[]) => setOpenTrigger(openKeys.includes(RoutePath.SETTINGS) ? 'hover' : 'click');
  const onMenuSelect = useCallback<MenuProps['onSelect']>((info) => {
    if (info.key === RoutePath.GLOB_HS) {
      GLOB.setAppState({ isHsOpen: true, });
      return;
    }

    if (info.key === RoutePath.FAVORITES) {
      GLOB.setAppState({ isFavoritesOpen: true });
      return;
    }

    if (info.key === RoutePath.SEARCH) {
      useAppGlobStore.setState({ topMenuSelectedKeys: [...topMenuSelectedKeys, info.key] });
      return;
    }

    useAppGlobStore.setState({ topMenuSelectedKeys: [info.key] });
  }, [topMenuSelectedKeys]);

  return (
    showMenu && <nav className='xm-top-menu'>
      {isMobile && !!menuItemsFetched.length && (
        <HamburgerMenuButton openLeftMenu={true} />
      )}
      <Menu key='class-menu' className='xm-menu-classes'
        mode="horizontal"
        subMenuCloseDelay={0.2}
        subMenuOpenDelay={0.3}
        selectedKeys={topMenuSelectedKeys}
        onSelect={onMenuSelect}
        items={menuItemsFetched.length ? menuItemsFetched : [{
          key: 'loading',
        }]}
      />
      {menuItemsFetched.length ? /** Wait for menu item fetch*/
        <Menu key='slim-menu' className='xm-menu-slim'
          mode="horizontal"
          disabledOverflow
          triggerSubMenuAction={'click'} // openTriger => Hover would be nice, but the requirement is that the submenu should not close on mouseleave.
          // onOpenChange={handleOpen}
          selectedKeys={topMenuSelectedKeys}
          onSelect={onMenuSelect}
          items={menuItemsStatic}
        /> : <></>
      }
    </nav>
  );
}

const makeChildRight = ({ path }: MenuNode) => (child: MenuNode): ItemType => {
  if (child.divider) {
    return {
      type: 'divider',
      key: child.key
    };
  }

  if (child.path) {
    if (!child.children) {
      return {
        key: child.key,
        label: <Link to={(path || '') + child.path}><>{child.title}</></Link>
      };
    }

    if (child.path === RoutePath.HELP) { // move self_monitoring right after Logs
      const monitor = child.children.find(({ path }) => path && path === RoutePath.SET_SELF_MONITORING);
      if (monitor) {
        const newChildenOrder = child.children.filter(({ path }) => path && path !== RoutePath.SET_SELF_MONITORING);
        const logIndex = newChildenOrder.map(({ key }) => key).indexOf(RoutePath.SET_LOGS);

        child.children = [
          ...newChildenOrder.slice(0, logIndex + 1),
          monitor,
          ...newChildenOrder.slice(logIndex + 1)
        ];
      }
    }

    let sharedNodeProps: ItemType = { type: 'group' };
    if ((SUBMENU_TYPE_MENU_ITEMS as readonly string[]).includes(child.key as string)) {
      sharedNodeProps = {
        className: 'xm-submenu',
        popupClassName: SUBMENU_POPUP_CLASS,
        children: undefined,
        key: undefined
      };
    }

    return {
      ...sharedNodeProps,
      key: child.key as string,
      label: child.title,
      children: child.children.map(leaf => {
        if (leaf.divider) {
          return {
            type: 'divider',
            key: leaf.key
          };
        }

        return {
          key: leaf.key,
          label: leaf.path ? <Link to={path + child.path + leaf.path}><>{leaf.title}</></Link> : leaf.title
        };
      })
    };
  }

  return {
    type: child.children ? 'group' : 'item',
    key: child.key,
    label: child.title,
    disabled: child.disabled,
  };
};

const SUBMENU_POPUP_CLASS = 'xm-submenu-top';
const SUBMENU_TYPE_MENU_ITEMS = [RoutePath.HELP] as const;

export default TopNavBar;
